import React from "react";
import {Progress} from "reactstrap";
import {ProgressEvent} from "../../hooks/useWebSocket";

interface UploadProgressProps {
  progress: number;
  message: string;
  stage: string;
  progressEvent?: ProgressEvent;
  isWebSocketConnected?: boolean;
  isReconnecting?: boolean;
  reconnectAttempts?: number;
}

const UploadProgress: React.FC<UploadProgressProps> = ({
  progress,
  message,
  stage,
  progressEvent,
  isWebSocketConnected = false,
  isReconnecting = false,
  reconnectAttempts = 0
}) => {
  // Determine progress bar color based on stage
  const getProgressColor = (currentStage: string): string => {
    switch (currentStage.toLowerCase()) {
      case "initialization":
      case "preparing upload":
        return "info";
      case "uploading file":
        return "warning";
      case "preprocessing":
      case "parsing":
      case "analysis":
      case "validation":
        return "primary";
      case "complete":
        return "success";
      case "error":
        return "danger";
      default:
        return "info";
    }
  };

  // Use WebSocket progress event data if available, otherwise fallback to props
  const displayProgress = progressEvent?.progress ?? progress;
  const displayMessage = progressEvent?.message ?? message;
  const displayStage = progressEvent?.stage ?? stage;

  return (
    <div className="upload-progress-container">
      <div className="progress-info-container">
        <div className="stage-info">
          <small className="text-muted">
            {isReconnecting ? (
              <span className="text-warning mr-2">
                <i className="fa fa-sync fa-spin" /> Reconnecting (attempt {reconnectAttempts + 1})
              </span>
            ) : isWebSocketConnected ? (
              <span className="text-success mr-2">
                <i className="fa fa-wifi" /> Live Updates
              </span>
            ) : (
              <span className="text-muted mr-2">
                <i className="fa fa-wifi" /> Connecting...
              </span>
            )}
            Stage: {displayStage}
          </small>
        </div>
        <div className="progress-percentage">
          <small className="text-muted">{displayProgress}%</small>
        </div>
      </div>

      <Progress
        value={displayProgress}
        color={getProgressColor(displayStage)}
        className="mb-3"
        striped
        animated={displayProgress < 100}
      />

      <div className="text-center">
        <p className="statusMessage mb-0">{displayMessage}</p>
        {progressEvent?.timestamp && (
          <small className="text-muted">Last updated: {new Date(progressEvent.timestamp).toLocaleTimeString()}</small>
        )}
      </div>
    </div>
  );
};

export default UploadProgress;
